import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  TextField,
  MenuItem,
  Stack,
  FormControlLabel,
  Switch,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  Grid,
  Divider
} from '@mui/material';
import { Edit, Info } from '@mui/icons-material';
import { PageIndices, TabIndices } from '../../common/i-doc/i-doc.registry.ts';
import { DashboardLayout } from '@components/DashboardLayout.tsx';
import {
  getAdminExpertPlans,
  updateAdminExpertPlan,
  getAdminExpertSubscriptions,
  getAdminExpertTransactions,
  getAdminExpertWebhookLogs,
} from '@services/payment.service.ts';
import {
  SubscriptionPlan,
  AdminSubscription,
  Transaction,
  WebhookLog,
  UpdatePlanRequest,
} from '@/types/payment.ts';
import { DevSubscriptionTools } from '@components/settings/DevSubscriptionTools.tsx';
import { UniversalPagination } from '@components/common/UniversalPagination.tsx';
import { toastService } from '@services/toast.service.ts';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
  tabId: string;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, tabId, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`expert-finance-tabpanel-${index}`}
      aria-labelledby={`expert-finance-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }} data-i-doc={tabId}>
          {children}
        </Box>
      )}
    </div>
  );
}

export const AdminExpertFinancePage: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);

  return (
    <DashboardLayout>
      <Box sx={{ width: '100%' }} data-i-doc={PageIndices.ADMIN_EXPERT_FINANCE}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} aria-label="expert finance tabs">
            <Tab label="Тарифні плани" />
            <Tab label="Підписки" />
            <Tab label="Транзакції" />
            <Tab label="Логи вебхуків" />
            <Tab label="Dev Tools" />
          </Tabs>
        </Box>
        <TabPanel value={tabValue} index={0} tabId={TabIndices.ADMIN_EXPERT_FINANCE_PLANS}>
          <ExpertPlansTab />
        </TabPanel>
        <TabPanel value={tabValue} index={1} tabId={TabIndices.ADMIN_EXPERT_FINANCE_SUBSCRIPTIONS}>
          <ExpertSubscriptionsTab />
        </TabPanel>
        <TabPanel value={tabValue} index={2} tabId={TabIndices.ADMIN_EXPERT_FINANCE_TRANSACTIONS}>
          <ExpertTransactionsTab />
        </TabPanel>
        <TabPanel value={tabValue} index={3} tabId={TabIndices.ADMIN_EXPERT_FINANCE_WEBHOOKS}>
          <ExpertWebhookLogsTab />
        </TabPanel>
        <TabPanel value={tabValue} index={4} tabId={TabIndices.ADMIN_EXPERT_FINANCE_DEV_TOOLS}>
          <ExpertDevToolsTab />
        </TabPanel>
      </Box>
    </DashboardLayout>
  );
};

// ─── Plans ───────────────────────────────────────────────────────────────────

const FEATURE_LABELS: Record<string, string> = {
  expert_posts: 'Пости',
  bulk_notifications: 'Масові сповіщення',
  client_mailings: 'Розсилки активності',
};

const ExpertPlansTab: React.FC = () => {
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [editingPlan, setEditingPlan] = useState<SubscriptionPlan | null>(null);
  const [editForm, setEditForm] = useState<UpdatePlanRequest>({});
  const [saving, setSaving] = useState(false);
  const [hasTrial, setHasTrial] = useState(false);

  const fetchPlans = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getAdminExpertPlans(includeInactive);
      setPlans(Array.isArray(data) ? data : []);
    } catch (err: any) {
      setError(err.message || 'Не вдалося завантажити тарифні плани');
      setPlans([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPlans(); }, [includeInactive]);

  const handleEditClick = (plan: SubscriptionPlan) => {
    setEditingPlan(plan);
    setHasTrial(!!(plan.trialPrice && plan.trialDays));
    setEditForm({
      name: plan.name,
      description: plan.description,
      isActive: plan.isActive,
      priceMonth: plan.priceMonth ? parseFloat(plan.priceMonth) : undefined,
      priceYear: plan.priceYear ? parseFloat(plan.priceYear) : undefined,
      trialPrice: plan.trialPrice ? parseFloat(plan.trialPrice) : undefined,
      trialDays: plan.trialDays,
      price: plan.price ? parseFloat(plan.price) : undefined,
    });
  };

  const handleCloseEdit = () => { setEditingPlan(null); setEditForm({}); setHasTrial(false); };

  const handleSaveEdit = async () => {
    if (!editingPlan) return;
    setSaving(true);
    try {
      const dataToSend = { ...editForm };
      if (!hasTrial) { dataToSend.trialPrice = 0; dataToSend.trialDays = 0; }
      await updateAdminExpertPlan(editingPlan.id, dataToSend);
      toastService.success('Тарифний план оновлено');
      handleCloseEdit();
      fetchPlans();
    } catch (err: any) {
      toastService.error(err.message || 'Не вдалося оновити план');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <FormControlLabel
          control={<Switch checked={includeInactive} onChange={(e) => setIncludeInactive(e.target.checked)} />}
          label="Показати неактивні"
        />
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? <CircularProgress /> : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Назва</TableCell>
                <TableCell>Код</TableCell>
                <TableCell>Ціна (Місяць)</TableCell>
                <TableCell>Ціна (Рік)</TableCell>
                <TableCell>Тріал</TableCell>
                <TableCell>Фічі</TableCell>
                <TableCell>Статус</TableCell>
                <TableCell>Дії</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {plans.length > 0 ? plans.map((plan) => (
                <TableRow key={plan.id}>
                  <TableCell>{plan.id}</TableCell>
                  <TableCell>{plan.name}</TableCell>
                  <TableCell>{plan.code}</TableCell>
                  <TableCell>{plan.priceMonth ? `${plan.priceMonth} ${plan.currency}` : plan.price}</TableCell>
                  <TableCell>{plan.priceYear ? `${plan.priceYear} ${plan.currency}` : '-'}</TableCell>
                  <TableCell>
                    {(plan.trialPrice && plan.trialDays) ? (
                      <Chip label={`${plan.trialPrice} ${plan.currency} / ${plan.trialDays} дн.`} color="info" size="small" variant="outlined" />
                    ) : '-'}
                  </TableCell>
                  <TableCell>
                    {plan.features && plan.features.length > 0 ? (
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                        {plan.features.map((f) => (
                          <Chip key={f} label={FEATURE_LABELS[f] ?? f} size="small" variant="outlined" color="primary" />
                        ))}
                      </Stack>
                    ) : (
                      <Typography variant="caption" color="text.secondary">базовий доступ</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip label={plan.isActive ? 'Активний' : 'Неактивний'} color={plan.isActive ? 'success' : 'default'} size="small" />
                  </TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => handleEditClick(plan)}><Edit /></IconButton>
                  </TableCell>
                </TableRow>
              )) : (
                <TableRow>
                  <TableCell colSpan={9} align="center">{error ? 'Помилка завантаження' : 'Немає даних'}</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={!!editingPlan} onClose={handleCloseEdit} maxWidth="sm" fullWidth>
        <DialogTitle>Редагування плану: {editingPlan?.name}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField label="Назва" value={editForm.name || ''} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} fullWidth />
            <TextField label="Опис" value={editForm.description || ''} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} fullWidth multiline rows={2} />
            <Divider sx={{ my: 1 }}>Ціни</Divider>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField label="Ціна (Місяць)" type="number" value={editForm.priceMonth || ''} onChange={(e) => setEditForm({ ...editForm, priceMonth: parseFloat(e.target.value) })} fullWidth />
              </Grid>
              <Grid item xs={6}>
                <TextField label="Ціна (Рік)" type="number" value={editForm.priceYear || ''} onChange={(e) => setEditForm({ ...editForm, priceYear: parseFloat(e.target.value) })} fullWidth />
              </Grid>
            </Grid>
            <Divider sx={{ my: 1 }}>Тріал (Пробний період)</Divider>
            <FormControlLabel
              control={<Switch checked={hasTrial} onChange={(e) => { setHasTrial(e.target.checked); if (e.target.checked && !editForm.trialPrice) setEditForm({ ...editForm, trialPrice: 1, trialDays: 30 }); }} />}
              label="Увімкнути тріал"
            />
            {hasTrial && (
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField label="Ціна тріалу" type="number" value={editForm.trialPrice || ''} onChange={(e) => setEditForm({ ...editForm, trialPrice: parseFloat(e.target.value) })} fullWidth />
                </Grid>
                <Grid item xs={6}>
                  <TextField label="Тривалість (днів)" type="number" value={editForm.trialDays || ''} onChange={(e) => setEditForm({ ...editForm, trialDays: parseInt(e.target.value) })} fullWidth />
                </Grid>
              </Grid>
            )}
            <Divider sx={{ my: 1 }} />
            <FormControlLabel
              control={<Switch checked={editForm.isActive || false} onChange={(e) => setEditForm({ ...editForm, isActive: e.target.checked })} />}
              label="Активний"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEdit} disabled={saving}>Скасувати</Button>
          <Button onClick={handleSaveEdit} variant="contained" disabled={saving}>
            {saving ? 'Збереження...' : 'Зберегти'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// ─── Subscriptions ────────────────────────────────────────────────────────────

const ExpertSubscriptionsTab: React.FC = () => {
  const [subscriptions, setSubscriptions] = useState<AdminSubscription[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit, setLimit] = useState(20);
  const [statusFilter, setStatusFilter] = useState('');
  const [userIdFilter, setUserIdFilter] = useState('');

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      setError(null);
      try {
        const offset = (page - 1) * limit;
        const userId = userIdFilter ? parseInt(userIdFilter) : undefined;
        const data = await getAdminExpertSubscriptions(limit, offset, statusFilter || undefined, userId);
        if (data && Array.isArray(data.items)) {
          setSubscriptions(data.items);
          setTotal(data.total);
        } else {
          setSubscriptions([]);
          setTotal(0);
        }
      } catch (err: any) {
        setError(err.message || 'Не вдалося завантажити підписки');
        setSubscriptions([]);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [page, statusFilter, userIdFilter, limit]);

  return (
    <Box>
      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <TextField
          select
          label="Статус"
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          sx={{ width: 200 }}
        >
          <MenuItem value="">Всі</MenuItem>
          <MenuItem value="active">Active</MenuItem>
          <MenuItem value="pending">Pending</MenuItem>
          <MenuItem value="cancelled">Cancelled</MenuItem>
          <MenuItem value="expired">Expired</MenuItem>
          <MenuItem value="payment_failed">Payment Failed</MenuItem>
        </TextField>
        <TextField
          label="User ID"
          value={userIdFilter}
          onChange={(e) => { setUserIdFilter(e.target.value); setPage(1); }}
          type="number"
        />
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? <CircularProgress /> : (
        <>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Експерт</TableCell>
                  <TableCell>План</TableCell>
                  <TableCell>Сума</TableCell>
                  <TableCell>Цикл</TableCell>
                  <TableCell>Статус</TableCell>
                  <TableCell>Наступна оплата</TableCell>
                  <TableCell>Створено</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {subscriptions.length > 0 ? subscriptions.map((sub) => (
                  <TableRow key={sub.id}>
                    <TableCell>{sub.id}</TableCell>
                    <TableCell>{sub.user.name} {sub.user.surname} ({sub.user.email})</TableCell>
                    <TableCell>{sub.plan?.name ?? '—'}</TableCell>
                    <TableCell>{sub.amount}</TableCell>
                    <TableCell>{(sub as any).billingCycle ?? '—'}</TableCell>
                    <TableCell>
                      <Chip label={sub.status} color={getSubscriptionStatusColor(sub.status)} size="small" />
                    </TableCell>
                    <TableCell>{sub.nextPaymentDate ? new Date(sub.nextPaymentDate).toLocaleDateString() : '—'}</TableCell>
                    <TableCell>{new Date(sub.createdAt).toLocaleDateString()}</TableCell>
                  </TableRow>
                )) : (
                  <TableRow>
                    <TableCell colSpan={8} align="center">{error ? 'Помилка завантаження' : 'Немає даних'}</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <UniversalPagination
            page={page}
            count={Math.ceil(total / limit)}
            limit={limit}
            total={total}
            onPageChange={(p) => setPage(p)}
            onLimitChange={(l) => { setLimit(l); setPage(1); }}
            storageKey="pagination:admin-expert-subscriptions"
            limitOptions={[10, 20, 50]}
          />
        </>
      )}
    </Box>
  );
};

// ─── Transactions ─────────────────────────────────────────────────────────────

const ExpertTransactionsTab: React.FC = () => {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit, setLimit] = useState(20);
  const [statusFilter, setStatusFilter] = useState('');
  const [userIdFilter, setUserIdFilter] = useState('');

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      setError(null);
      try {
        const offset = (page - 1) * limit;
        const userId = userIdFilter ? parseInt(userIdFilter) : undefined;
        const data = await getAdminExpertTransactions(limit, offset, statusFilter || undefined, userId);
        if (data && Array.isArray(data.items)) {
          setTransactions(data.items);
          setTotal(data.total);
        } else {
          setTransactions([]);
          setTotal(0);
        }
      } catch (err: any) {
        setError(err.message || 'Не вдалося завантажити транзакції');
        setTransactions([]);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [page, statusFilter, userIdFilter, limit]);

  return (
    <Box>
      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <TextField
          select
          label="Статус"
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          sx={{ width: 200 }}
        >
          <MenuItem value="">Всі</MenuItem>
          <MenuItem value="Approved">Approved</MenuItem>
          <MenuItem value="Declined">Declined</MenuItem>
          <MenuItem value="Created">Created</MenuItem>
          <MenuItem value="Refunded">Refunded</MenuItem>
        </TextField>
        <TextField
          label="User ID"
          value={userIdFilter}
          onChange={(e) => { setUserIdFilter(e.target.value); setPage(1); }}
          type="number"
        />
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? <CircularProgress /> : (
        <>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Order Ref</TableCell>
                  <TableCell>Експерт</TableCell>
                  <TableCell>Сума</TableCell>
                  <TableCell>Статус</TableCell>
                  <TableCell>Тип</TableCell>
                  <TableCell>Дата</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {transactions.length > 0 ? transactions.map((tx) => (
                  <TableRow key={tx.id}>
                    <TableCell>{tx.id}</TableCell>
                    <TableCell>{tx.orderReference}</TableCell>
                    <TableCell>{tx.user?.email || `User ID: ${tx.userId}`}</TableCell>
                    <TableCell>{tx.amount} {tx.currency}</TableCell>
                    <TableCell>
                      <Chip label={tx.status} color={getTransactionStatusColor(tx.status)} size="small" />
                    </TableCell>
                    <TableCell>{tx.type}</TableCell>
                    <TableCell>{new Date(tx.createdAt).toLocaleString()}</TableCell>
                  </TableRow>
                )) : (
                  <TableRow>
                    <TableCell colSpan={7} align="center">{error ? 'Помилка завантаження' : 'Немає даних'}</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <UniversalPagination
            page={page}
            count={Math.ceil(total / limit)}
            limit={limit}
            total={total}
            onPageChange={(p) => setPage(p)}
            onLimitChange={(l) => { setLimit(l); setPage(1); }}
            storageKey="pagination:admin-expert-transactions"
            limitOptions={[10, 20, 50]}
          />
        </>
      )}
    </Box>
  );
};

// ─── Webhook Logs ─────────────────────────────────────────────────────────────

const ExpertWebhookLogsTab: React.FC = () => {
  const [logs, setLogs] = useState<WebhookLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit, setLimit] = useState(20);
  const [orderRefFilter, setOrderRefFilter] = useState('');
  const [selectedLog, setSelectedLog] = useState<WebhookLog | null>(null);

  useEffect(() => {
    const fetchLogs = async () => {
      setLoading(true);
      setError(null);
      try {
        const offset = (page - 1) * limit;
        const data = await getAdminExpertWebhookLogs(limit, offset, orderRefFilter || undefined);
        setLogs(data.items);
        setTotal(data.total);
      } catch (err: any) {
        setError(err.message || 'Не вдалося завантажити логи вебхуків');
        setLogs([]);
      } finally {
        setLoading(false);
      }
    };
    fetchLogs();
  }, [page, orderRefFilter, limit]);

  return (
    <Box>
      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <TextField
          label="Order Reference"
          value={orderRefFilter}
          onChange={(e) => { setOrderRefFilter(e.target.value); setPage(1); }}
          sx={{ width: 300 }}
        />
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? <CircularProgress /> : (
        <>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Order Ref</TableCell>
                  <TableCell>Статус</TableCell>
                  <TableCell>Помилка</TableCell>
                  <TableCell>Дата</TableCell>
                  <TableCell>Деталі</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {logs.length > 0 ? logs.map((log) => (
                  <TableRow key={log.id}>
                    <TableCell>{log.id}</TableCell>
                    <TableCell>{log.orderReference}</TableCell>
                    <TableCell>
                      <Chip label={log.status} color={log.status === 'processed' ? 'success' : 'error'} size="small" />
                    </TableCell>
                    <TableCell>{log.errorMessage || '-'}</TableCell>
                    <TableCell>{new Date(log.createdAt).toLocaleString()}</TableCell>
                    <TableCell>
                      <IconButton size="small" onClick={() => setSelectedLog(log)}><Info /></IconButton>
                    </TableCell>
                  </TableRow>
                )) : (
                  <TableRow>
                    <TableCell colSpan={6} align="center">{error ? 'Помилка завантаження' : 'Немає даних'}</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <UniversalPagination
            page={page}
            count={Math.ceil(total / limit)}
            limit={limit}
            total={total}
            onPageChange={(p) => setPage(p)}
            onLimitChange={(l) => { setLimit(l); setPage(1); }}
            storageKey="pagination:admin-expert-webhook-logs"
            limitOptions={[10, 20, 50]}
          />
        </>
      )}

      <Dialog open={!!selectedLog} onClose={() => setSelectedLog(null)} maxWidth="md" fullWidth>
        <DialogTitle>Деталі вебхуку #{selectedLog?.id}</DialogTitle>
        <DialogContent>
          <Typography variant="subtitle2" gutterBottom>Payload:</Typography>
          <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f5f5f5', overflow: 'auto', maxHeight: 400 }}>
            <pre style={{ margin: 0 }}>{JSON.stringify(selectedLog?.payload, null, 2)}</pre>
          </Paper>
          {selectedLog?.errorMessage && (
            <>
              <Typography variant="subtitle2" color="error" sx={{ mt: 2 }} gutterBottom>Error:</Typography>
              <Paper variant="outlined" sx={{ p: 2, bgcolor: '#fff0f0' }}>
                <Typography color="error">{selectedLog.errorMessage}</Typography>
              </Paper>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedLog(null)}>Закрити</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// ─── Dev Tools ────────────────────────────────────────────────────────────────

const ExpertDevToolsTab: React.FC = () => {
  return (
    <Box>
      <DevSubscriptionTools domain="expert" />
    </Box>
  );
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const getSubscriptionStatusColor = (status: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
  switch (status) {
    case 'active': return 'success';
    case 'pending': return 'warning';
    case 'cancelled': return 'default';
    case 'expired': return 'error';
    case 'payment_failed': return 'error';
    default: return 'default';
  }
};

const getTransactionStatusColor = (status: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
  switch (status) {
    case 'Approved': return 'success';
    case 'Declined': return 'error';
    case 'Created': return 'info';
    case 'Refunded': return 'warning';
    default: return 'default';
  }
};
